'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createClient } from '../lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Singleton auth client — one instance for the entire app lifecycle
const supabase = createClient();

const clearStaleTokens = () => {
  try {
    Object.keys(localStorage)
      .filter(k =>
        k.includes('supabase') ||
        k.includes('sb-') ||
        k.includes('sb_') ||
        k === 'ibdaa-auth-token'
      )
      .forEach(k => localStorage.removeItem(k));
  } catch {}
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    let settled = false;

    // Subscribe to auth state changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      settled = true;

      if (_event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }

      setSession(newSession);
      setUser(newSession?.user ?? null);
      setLoading(false);
    });

    // Fallback: if onAuthStateChange doesn't fire within 3s, resolve loading
    const fallbackTimer = setTimeout(() => {
      if (!settled) {
        setLoading(false);
      }
    }, 3000);

    // Eagerly get session once — don't race with onAuthStateChange
    supabase.auth.getSession().then(({ data: { session: initialSession }, error }) => {
      if (error) {
        const msg = error.message || '';
        if (
          msg.includes('Refresh Token Not Found') ||
          msg.includes('Invalid Refresh Token') ||
          msg.includes('AbortError') ||
          msg.includes('Lock') ||
          msg.includes('lock')
        ) {
          clearStaleTokens();
          supabase.auth.signOut({ scope: 'local' }).catch(() => {});
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }
      }

      // Only apply if onAuthStateChange hasn't already settled
      if (!settled) {
        setSession(initialSession ?? null);
        setUser(initialSession?.user ?? null);
        setLoading(false);
      }
    }).catch(() => {
      if (!settled) {
        setLoading(false);
      }
    });

    return () => {
      clearTimeout(fallbackTimer);
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata = {}) => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${siteUrl}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async () => {
    if (!user?.email) return null;
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('email', user.email)
      .maybeSingle();
    if (error) throw error;
    return data;
  };

  const getClientProfile = async () => {
    if (!user?.email) return null;
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('email', user.email)
      .maybeSingle();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    getClientProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
